/**
 * Plugin catalogs — import this module once at app boot to register builtins.
 *
 * To add a topology / init / lens / model:
 *   1. Create a file under the matching folder exporting a plugin object
 *   2. Register it here (or side-effect register inside the file and import it)
 *   3. Reference its `id` from shared preset JSON or the UI
 */

import type { Registry } from "../core/registry";
import type {
  AudioVoicePlugin,
  InitPlugin,
  LensPlugin,
  ModelPlugin,
  TopologyPlugin,
} from "./types";
import { Registry as Reg } from "../core/registry";
import { gravityDroneVoice } from "../audio/voices/gravityDrone";
import { kinematicsPadVoice } from "../audio/voices/kinematicsPad";
import { topologyGrainsVoice } from "../audio/voices/topologyGrains";

export const initRegistry: Registry<InitPlugin> = new Reg();
export const topologyRegistry: Registry<TopologyPlugin> = new Reg();
export const modelRegistry: Registry<ModelPlugin> = new Reg();
export const lensRegistry: Registry<LensPlugin> = new Reg();
export const audioVoiceRegistry: Registry<AudioVoicePlugin> = new Reg();

import {
  gaussianInit,
  gridInit,
  ringInit,
  sphereInit,
  sphericalShellInit,
  twoCampsInit,
  uniformBoxInit,
} from "./inits/builtin";
import { enemyEdgesLens, friendEdgesLens } from "./lenses/edges";
import { gnomonLens, grid3dLens } from "./lenses/grid3d";
import { selectionLens } from "./lenses/selection";
import { velocityVectorsLens } from "./lenses/velocityVectors";
import { friendsEnemiesModel } from "./models/friendsEnemies";
import { reynoldsModel } from "./models/reynolds";
import {
  multiRingTopology,
  randomTopology,
  ringChainTopology,
} from "./topologies/builtin";
import {
  tribesBlocksTopology,
  tribesRandomTopology,
} from "./topologies/tribes";
import {
  voxelFace6Topology,
  voxelLayeredTopology,
  voxelMoore26Topology,
} from "./topologies/voxel";

/** Topologies that require typeCount > 1 — omit from dropdown when T=1. */
export const REQUIRES_TYPES = new Set(["tribes_random", "tribes_blocks"]);

let loaded = false;

/** Idempotent builtin registration */
export function loadBuiltinPlugins(): void {
  if (loaded) return;
  loaded = true;

  initRegistry.register(uniformBoxInit);
  initRegistry.register(ringInit);
  initRegistry.register(gaussianInit);
  initRegistry.register(sphereInit);
  initRegistry.register(sphericalShellInit);
  initRegistry.register(gridInit);
  initRegistry.register(twoCampsInit);

  topologyRegistry.register(randomTopology);
  topologyRegistry.register(ringChainTopology);
  topologyRegistry.register(multiRingTopology);
  topologyRegistry.register(voxelFace6Topology);
  topologyRegistry.register(voxelMoore26Topology);
  topologyRegistry.register(voxelLayeredTopology);
  topologyRegistry.register(tribesBlocksTopology);
  topologyRegistry.register(tribesRandomTopology);

  modelRegistry.register(friendsEnemiesModel);
  modelRegistry.register(reynoldsModel);

  lensRegistry.register(friendEdgesLens);
  lensRegistry.register(enemyEdgesLens);
  lensRegistry.register(selectionLens);
  lensRegistry.register(grid3dLens);
  lensRegistry.register(gnomonLens);
  lensRegistry.register(velocityVectorsLens);

  audioVoiceRegistry.register(kinematicsPadVoice);
  audioVoiceRegistry.register(gravityDroneVoice);
  audioVoiceRegistry.register(topologyGrainsVoice);
}

export type {
  AudioVoicePlugin,
  DrawContext,
  InitPlugin,
  LensPlugin,
  ModelPlugin,
  TopologyPlugin,
} from "./types";
